/**
 * Qualitäts-Gates — reine Entscheidung aus bereits erhobenen Signalen.
 *
 * Der Dienst lädt Pipeline, Auto-Q&A und eine mögliche Übersteuerung aus der
 * Datenbank. Diese Datei entscheidet anschließend ohne Seiteneffekt, ob ein
 * Merge erlaubt ist. Damit benutzen Oberfläche, tRPC, MCP und der eigentliche
 * Merge-Pfad dieselbe Regel statt vier ähnlich klingender Prüfungen.
 */
import { SCHWEREGRADE, } from "./durchsicht-befund.js";
export const QUALITAETS_GATE_MODI = [
    "aus",
    "schatten",
    "blockierend",
];
export const QUALITAETS_GATE_SCHWELLEN = ["schwer", "kritisch"];
export const QUALITAETS_GATE_ENTSCHEIDUNGEN = [
    "bestanden",
    "ausstehend",
    "blockiert",
    "nicht_bewertbar",
    "uebersteuert",
];
export const QUALITAETS_GATE_GRUENDE = [
    "kopf_sha_fehlt",
    "pipeline_fehlt",
    "pipeline_laeuft",
    "pipeline_veraltet",
    "pipeline_gescheitert",
    "auto_qa_fehlt",
    "auto_qa_laeuft",
    "auto_qa_veraltet",
    "auto_qa_automation_veraltet",
    "auto_qa_nicht_bewertbar",
    "befunde_blockieren",
];
/**
 * Sichere Vorgabe: erst beobachten, dann ausdrücklich scharf schalten.
 *
 * `autoQaErforderlich` steht auf `false`, weil `automationIds` leer ist. Beides
 * zusammen wäre ein Pflichtsignal, das per Konstruktion nie erfüllt werden
 * kann: Die Bewertung meldet `auto_qa_fehlt`, und der Merge Request bleibt
 * für immer `ausstehend`. Genau so stand es bis zum 21.08.2026 hier — jeder
 * Merge Request beider Projekte hing an einem Signal, das niemand ausgewählt
 * hatte, und das Gate meldete nur noch sich selbst.
 *
 * Auto-Q&A wird zum Pflichtsignal, sobald jemand eine Automation auswählt.
 * Dass beides zusammengehört, setzt `qualitaetsGateRegelFehler()` in der
 * Oberfläche und `aendereQualitaetsGateRegel()` am Server durch.
 */
export const QUALITAETS_GATE_VORGABE = {
    modus: "schatten",
    pipelineErforderlich: true,
    autoQaErforderlich: false,
    schwereGrenze: "schwer",
    automationIds: [],
};
const PIPELINE_OFFEN = new Set([
    "created",
    "waiting_for_resource",
    "preparing",
    "pending",
    "running",
    "canceling",
    "scheduled",
    "manual",
]);
/**
 * Kann sich an dieser Pipeline noch etwas ändern?
 *
 * `false` heißt Endzustand: `success`, `failed`, `canceled`, `skipped`. Wer
 * eine solche Pipeline erneut abfragt, bekommt garantiert dasselbe zurück —
 * und wird sie erst wieder hören, wenn ein Neulauf sie in einen offenen
 * Zustand zurückversetzt. Genau daran hing bis zum 23.08.2026 ein Beobachter,
 * der nie aufhörte: Der GitLab-Nachzug holte bei jedem Durchlauf zu jeder der
 * fünfzig jüngsten Pipelines Detail **und** Jobs, ganz gleich ob sie seit
 * Tagen fertig war. Bei zwei verknüpften Projekten waren das rund
 * zweihunderttausend API-Aufrufe am Tag für Daten, die sich nicht mehr ändern.
 *
 * Unbekannte Zustände gelten als offen: Lieber einmal zu viel nachfragen als
 * eine laufende Pipeline für fertig halten.
 */
export function istPipelineOffen(zustand) {
    const wert = (zustand ?? "").trim().toLowerCase();
    if (wert === "")
        return true;
    return PIPELINE_OFFEN.has(wert) || !PIPELINE_ENDZUSTAENDE.has(wert);
}
/** Die Zustände, nach denen GitLab nichts mehr ändert. */
const PIPELINE_ENDZUSTAENDE = new Set([
    "success",
    "failed",
    "canceled",
    "cancelled",
    "skipped",
]);
const AUTO_QA_OFFEN = new Set([
    "gebremst",
    "wartet",
    "laeuft",
]);
function schwereAb(schwelle) {
    const von = SCHWEREGRADE.indexOf(schwelle);
    return SCHWEREGRADE.slice(von);
}
function zaehleBlockierendeBefunde(autoQa, schwelle) {
    return schwereAb(schwelle).reduce((summe, schwere) => summe + (autoQa.offeneBefunde[schwere] ?? 0), 0);
}
function urspruenglicheEntscheidung(gruende) {
    if (gruende.some((grund) => grund.art === "nicht_bewertbar")) {
        return "nicht_bewertbar";
    }
    if (gruende.some((grund) => grund.art === "blockiert"))
        return "blockiert";
    if (gruende.length > 0)
        return "ausstehend";
    return "bestanden";
}
/**
 * Bewertet die Lage fail-closed: Nur passende, erfolgreiche Signale ergeben
 * `bestanden`. Im Schattenmodus bleibt der Merge trotzdem erlaubt; das
 * Ergebnis sagt mit `haetteBlockiert`, was beim Scharfschalten geschehen wäre.
 */
export function bewerteQualitaetsGate(lage) {
    if (lage.regel.modus === "aus") {
        return {
            entscheidung: "bestanden",
            urspruenglicheEntscheidung: "bestanden",
            gruende: [],
            darfMergen: true,
            haetteBlockiert: false,
        };
    }
    const gruende = [];
    if (!lage.kopfSha) {
        gruende.push({
            code: "kopf_sha_fehlt",
            art: "nicht_bewertbar",
            meldung: "Die Kopf-SHA des Merge Requests fehlt.",
        });
    }
    if (lage.regel.pipelineErforderlich) {
        if (!lage.pipeline) {
            gruende.push({
                code: "pipeline_fehlt",
                art: "ausstehend",
                meldung: "Für diese Revision liegt noch keine Pipeline vor.",
            });
        }
        else if (lage.kopfSha &&
            lage.pipeline.commitSha?.toLowerCase() !== lage.kopfSha.toLowerCase()) {
            gruende.push({
                code: "pipeline_veraltet",
                art: "ausstehend",
                meldung: "Die neueste Pipeline gehört zu einer älteren Revision.",
            });
        }
        else if (lage.pipeline.zustand !== "success") {
            const offen = PIPELINE_OFFEN.has(lage.pipeline.zustand);
            gruende.push({
                code: offen ? "pipeline_laeuft" : "pipeline_gescheitert",
                art: offen ? "ausstehend" : "blockiert",
                meldung: offen
                    ? "Die Pipeline ist noch nicht abgeschlossen."
                    : `Die Pipeline endete mit „${lage.pipeline.zustand}“ statt erfolgreich.`,
            });
        }
    }
    if (lage.regel.autoQaErforderlich) {
        if (lage.regel.automationIds.length === 0) {
            gruende.push({
                code: "auto_qa_fehlt",
                art: "ausstehend",
                meldung: "Für das Gate ist noch keine Auto-Q&A-Automation ausgewählt.",
            });
        }
        for (const automationId of lage.regel.automationIds) {
            const autoQa = lage.autoQa.find((signal) => signal.automationId === automationId);
            const name = autoQa?.automationName ?? automationId;
            if (!autoQa) {
                gruende.push({
                    code: "auto_qa_fehlt",
                    art: "ausstehend",
                    meldung: `Für „${name}“ liegt noch kein Auto-Q&A-Auftrag vor.`,
                    bezugId: automationId,
                });
            }
            else if (autoQa.automationAktiv === false ||
                (autoQa.automationVersion !== undefined &&
                    autoQa.aktuelleAutomationVersion !== undefined &&
                    autoQa.automationVersion !== autoQa.aktuelleAutomationVersion)) {
                gruende.push({
                    code: "auto_qa_automation_veraltet",
                    art: "ausstehend",
                    meldung: autoQa.automationAktiv === false
                        ? `„${name}“ ist nicht mehr aktiv. Für das Gate ist ein Auftrag einer aktiven Automation erforderlich.`
                        : `„${name}“ lief mit Automation v${autoQa.automationVersion}; aktuell ist v${autoQa.aktuelleAutomationVersion}. Eine neue Prüfung ist erforderlich.`,
                    bezugId: automationId,
                });
            }
            else if (AUTO_QA_OFFEN.has(autoQa.zustand) &&
                lage.kopfSha &&
                autoQa.kopfSha &&
                autoQa.kopfSha.toLowerCase() !== lage.kopfSha.toLowerCase()) {
                gruende.push({
                    code: "auto_qa_veraltet",
                    art: "ausstehend",
                    meldung: `„${name}“ prüft eine ältere Revision.`,
                    bezugId: automationId,
                });
            }
            else if (AUTO_QA_OFFEN.has(autoQa.zustand)) {
                gruende.push({
                    code: "auto_qa_laeuft",
                    art: "ausstehend",
                    meldung: `„${name}“ ist noch nicht abgeschlossen.`,
                    bezugId: automationId,
                });
            }
            else if (!autoQa.kopfSha) {
                gruende.push({
                    code: "auto_qa_nicht_bewertbar",
                    art: "nicht_bewertbar",
                    meldung: `„${name}“ endete ohne einen prüfbaren Lauf.`,
                    bezugId: automationId,
                });
            }
            else if (lage.kopfSha &&
                autoQa.kopfSha.toLowerCase() !== lage.kopfSha.toLowerCase()) {
                gruende.push({
                    code: "auto_qa_veraltet",
                    art: "ausstehend",
                    meldung: `„${name}“ prüfte eine ältere Revision.`,
                    bezugId: automationId,
                });
            }
            else if (autoQa.zustand === "gescheitert" ||
                autoQa.zustand === "abgebrochen" ||
                autoQa.nichtBewertbar === true) {
                gruende.push({
                    code: "auto_qa_nicht_bewertbar",
                    art: "nicht_bewertbar",
                    meldung: `„${name}“ konnte diese Revision nicht vollständig bewerten.`,
                    bezugId: automationId,
                });
            }
            else {
                const anzahl = zaehleBlockierendeBefunde(autoQa, lage.regel.schwereGrenze);
                if (anzahl > 0) {
                    gruende.push({
                        code: "befunde_blockieren",
                        art: "blockiert",
                        meldung: `${anzahl} offener Befund aus „${name}“ ab „${lage.regel.schwereGrenze}“ hält den Merge auf.`,
                        anzahl,
                        bezugId: automationId,
                    });
                }
            }
        }
    }
    const ursprung = urspruenglicheEntscheidung(gruende);
    const haetteBlockiert = ursprung !== "bestanden";
    const uebersteuert = (ursprung === "blockiert" || ursprung === "nicht_bewertbar") &&
        lage.uebersteuert;
    const entscheidung = uebersteuert ? "uebersteuert" : ursprung;
    const darfMergen = lage.regel.modus !== "blockierend" ||
        ursprung === "bestanden" ||
        uebersteuert;
    return {
        entscheidung,
        urspruenglicheEntscheidung: ursprung,
        gruende,
        darfMergen,
        haetteBlockiert,
    };
}
//# sourceMappingURL=qualitaets-gate.js.map